<template>
    <div class="calendar-container">
        <!-- 日历头部 - 星期 -->
        <div class="calendar-header">
            <div class="weekday" v-for="day in weekdays" :key="day">{{ day }}</div>
        </div>

        <!-- 日历主体 -->
        <div class="calendar-body">
            <!-- 日期网格 -->
            <div v-for="(day, index) in calendarDays" :key="index" class="calendar-day" :class="getDayClass(day)"
                :style="getDayBackgroundStyle(day)">
                <!-- 上半部分：日期数字 -->
                <div class="day-section" :style="getDaySectionStyle(day)">
                    <!-- 任务开始标识色块 -->
                    <div v-if="isSegmentStartDay(day)" class="task-start-marker" :style="getTaskStartMarkerStyle(day)">
                    </div>
                    <div class="day-number">{{ day.day }}</div>
                </div>

                <!-- 下半部分：任务信息 -->
                <div class="task-section">
                    <!-- 移除原有的任务显示逻辑 -->
                </div>

                <!-- 为有任务或生长阶段的日期添加tooltip -->
                <el-tooltip v-if="day.task || getGrowthStageForDay(day)" placement="top" effect="dark"
                    popper-class="task-tooltip">
                    <div slot="content">
                        <div class="tooltip-content">
                            <!-- 任务信息 -->
                            <div v-if="day.task">
                                <div class="tooltip-title">{{ day.task.categoryName }}</div>
                                <div class="tooltip-item">
                                    <span class="tooltip-label">当前状态：</span>
                                    <span class="tooltip-value" :style="{ color: getTaskMarkerColor(day.task) }">{{
                                        getTaskStatus(day.task) }}</span>
                                </div>
                                <div class="tooltip-item">
                                    <span class="tooltip-label">执行人：</span>
                                    <span class="tooltip-value">{{ day.task.executorNickName ||
                                        day.task.executorUsername
                                        }}</span>
                                </div>
                                <div class="tooltip-item">
                                    <span class="tooltip-label">开始时间：</span>
                                    <span class="tooltip-value">{{ dayjs(day.task.execStartDate).format('YYYY-MM-DD')
                                    }}</span>
                                </div>
                                <div class="tooltip-item">
                                    <span class="tooltip-label">结束时间：</span>
                                    <span class="tooltip-value">{{ dayjs(day.task.execEndDate).format('YYYY-MM-DD')
                                    }}</span>
                                </div>
                                <div class="tooltip-item">
                                    <span class="tooltip-label">持续天数：</span>
                                    <span class="tooltip-value">{{
                                        dayjs(day.task.execEndDate).diff(dayjs(day.task.execStartDate),
                                            'day') + 1 }}天</span>
                                </div>
                                <div class="tooltip-item">
                                    <el-button type="text" @click="handleView(day.task)">查看详情</el-button>
                                </div>
                            </div>
                            <!-- 生长阶段信息 -->
                            <div v-else-if="getGrowthStageForDay(day)">
                                <div class="tooltip-title">{{ getGrowthStageForDay(day).basicStageName }}</div>
                                <div class="tooltip-item">
                                    <span class="tooltip-label">开始时间：</span>
                                    <span class="tooltip-value">{{
                                        dayjs(getGrowthStageForDay(day).startDate).format('YYYY-MM-DD') }}</span>
                                </div>
                                <div class="tooltip-item">
                                    <span class="tooltip-label">结束时间：</span>
                                    <span class="tooltip-value">{{
                                        dayjs(getGrowthStageForDay(day).endDate).format('YYYY-MM-DD') }}</span>
                                </div>
                                <div class="tooltip-item">
                                    <span class="tooltip-label">持续天数：</span>
                                    <span class="tooltip-value">{{
                                        dayjs(getGrowthStageForDay(day).endDate).diff(dayjs(getGrowthStageForDay(day).startDate),
                                        'day') + 1 }}天</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="day-tooltip-trigger"></div>
                </el-tooltip>
            </div>

            <!-- 绝对定位的任务条 -->
            <div v-for="(task, taskIndex) in uniqueTasks" :key="taskIndex" class="task-bar"
                :style="getTaskBarStyle(task)">
                <el-tooltip placement="top" effect="dark" popper-class="task-tooltip">
                    <div slot="content">
                        <div class="tooltip-content">
                            <div class="tooltip-title">{{ task.categoryName }}</div>
                            <div class="tooltip-item">
                                <span class="tooltip-label">当前状态：</span>
                                <span class="tooltip-value" :style="{ color: getTaskMarkerColor(task) }">{{
                                    getTaskStatus(task) }}</span>
                            </div>
                            <div class="tooltip-item">
                                <span class="tooltip-label">执行人：</span>
                                <span class="tooltip-value">{{ task.executorNickName || task.executorUsername }}</span>
                            </div>
                            <div class="tooltip-item">
                                <span class="tooltip-label">开始时间：</span>
                                <span class="tooltip-value">{{ dayjs(task.execStartDate).format('YYYY-MM-DD') }}</span>
                            </div>
                            <div class="tooltip-item">
                                <span class="tooltip-label">结束时间：</span>
                                <span class="tooltip-value">{{ dayjs(task.execEndDate).format('YYYY-MM-DD') }}</span>
                            </div>
                            <div class="tooltip-item">
                                <span class="tooltip-label">持续天数：</span>
                                <span class="tooltip-value">{{ dayjs(task.execEndDate).diff(dayjs(task.execStartDate),
                                    'day') + 1 }}天</span>
                            </div>
                            <div class="tooltip-item">
                                <el-button type="text" @click="handleView(task)">查看详情</el-button>
                            </div>
                        </div>
                    </div>
                    <div class="task-bar-content">
                        <i class="el-icon-warning" v-if="task.execStatus === 'DELAYED'"
                            :style="{ color: getTaskMarkerColor(task) }"></i>
                        <i class="el-icon-success" v-else-if="task.execStatus === 'COMPLETED'"
                            :style="{ color: getTaskMarkerColor(task) }"></i>
                        <i class="el-icon-time" v-else :style="{ color: getTaskMarkerColor(task) }"></i>
                        <div class="task-status" :style="{ color: getTaskMarkerColor(task) }">
                            {{ getTaskStatus(task) }}
                        </div>
                        <div class="task-separator" :style="{ color: getTaskMarkerColor(task) }">：</div>
                        <div class="task-name" :style="{ color: getTaskMarkerColor(task) }">{{ task.categoryName }}
                        </div>
                    </div>
                </el-tooltip>
            </div>
        </div>
        <!-- 任务执行弹窗 -->
        <TaskExecutionDialog :visible.sync="viewDialogVisible" :taskData="selectedTask" :mode="dialogMode"
            @success="handleTaskSuccess" />
    </div>
</template>

<script>
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import axios from 'axios'
import { baseUrl } from '@/axios/baseUrl'
import TaskExecutionDialog from '../TaskExecutionDialog.vue'
// 扩展 dayjs
dayjs.extend(isBetween)

export default {
    name: 'Calendar',
    components: {
        TaskExecutionDialog
    },
    props: {
        greenhouseId: {
            type: [String, Number],
            default: ''
        },
        varietyId: {
            type: [String, Number],
            default: ''
        },
        yearMonth: {
            type: String,
            default: '2025-08'
        },
        currentStageInfo: {
            type: Object,
            default: () => ({
                name: '暂无',
                inPeriod: false,
                index: 0,
                allCycleDetailOptions: []
            })
        }
    },
    data() {
        return {
            weekdays: ['一', '二', '三', '四', '五', '六', '日'],
            calendarDays: [],
            taskData: [],
            selectedTask: {},
            dialogMode: 'view',
            viewDialogVisible: false
        }
    },
    computed: {
        // 计算当前月份的第一天和最后一天
        monthStart() {
            return dayjs(this.yearMonth + '-01')
        },
        monthEnd() {
            return this.monthStart.endOf('month')
        },
        // 获取唯一的任务列表
        uniqueTasks() {
            const tasks = []
            const taskIds = new Set()

            // 从日历数据中获取任务
            this.calendarDays.forEach(day => {
                if (day.task && !taskIds.has(day.task.id)) {
                    tasks.push(day.task)
                    taskIds.add(day.task.id)
                }
            })

            // 如果没有从日历数据中找到任务，则从原始任务数据中获取
            if (tasks.length === 0 && this.taskData.length > 0) {
                this.taskData.forEach(task => {
                    if (!taskIds.has(task.id)) {
                        const taskStartDate = dayjs(task.execStartDate)
                        const taskEndDate = dayjs(task.execEndDate)
                        const monthStart = this.monthStart
                        const monthEnd = this.monthEnd

                        // 检查任务是否与当前月份有重叠
                        if (taskEndDate.isAfter(monthStart) && taskStartDate.isBefore(monthEnd)) {
                            tasks.push(task)
                            taskIds.add(task.id)
                        }
                    }
                })
            }

            return tasks
        }
    },
    watch: {
        yearMonth: {
            handler() {
                this.generateCalendar()
                this.fetchTaskData()
            },
            immediate: true
        },
        greenhouseId: {
            handler() {
                this.fetchTaskData()
            }
        },
        varietyId: {
            handler() {
                this.fetchTaskData()
            }
        }
    },
    methods: {

        handleView(task) {
            console.log('查看详情', task)
            this.viewDialogVisible = true;
            this.selectedTask = task;
            this.dialogMode = 'view';
            // this.$emit('view', task)
        },
        handleTaskSuccess() {
            this.viewDialogVisible = false;
            this.selectedTask = {};
        },
        // 生成日历数据
        generateCalendar() {
            this.calendarDays = []

            // 获取当前月份的第一天和最后一天
            const firstDayOfMonth = this.monthStart
            const lastDayOfMonth = this.monthEnd

            // 计算当前月份第一天是星期几（0=周日，1=周一，...）
            const firstDayWeekday = firstDayOfMonth.day()
            // 转换为周一为0的格式
            const mondayFirstWeekday = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1

            // 计算需要填充的空格数量
            const emptyCells = mondayFirstWeekday

            // 添加空单元格
            for (let i = 0; i < emptyCells; i++) {
                this.calendarDays.push({
                    day: '',
                    date: '',
                    isCurrentMonth: false,
                    isToday: false,
                    isEmpty: true,
                    task: null
                })
            }

            // 添加当前月份的日期
            let currentDate = firstDayOfMonth
            while (currentDate.isBefore(lastDayOfMonth) || currentDate.isSame(lastDayOfMonth, 'day')) {
                this.calendarDays.push({
                    day: currentDate.date(),
                    date: currentDate.format('YYYY-MM-DD'),
                    isCurrentMonth: true,
                    isToday: currentDate.isSame(dayjs(), 'day'),
                    isEmpty: false,
                    task: null
                })
                currentDate = currentDate.add(1, 'day')
            }

            // 计算还需要多少个单元格来填满网格（确保是7的倍数）
            const totalCells = this.calendarDays.length
            const remainingCells = (7 - (totalCells % 7)) % 7

            // 添加剩余的空单元格
            for (let i = 0; i < remainingCells; i++) {
                this.calendarDays.push({
                    day: '',
                    date: '',
                    isCurrentMonth: false,
                    isToday: false,
                    isEmpty: true,
                    task: null
                })
            }

            // 应用任务数据到日历
            this.applyTaskData()
        },

        // 应用任务数据到日历
        applyTaskData() {
            // 首先清除所有日期的任务数据
            this.calendarDays.forEach(day => {
                if (!day.isEmpty) {
                    day.task = null
                }
            })

            // 如果没有任务数据，直接返回（已经清空了）
            if (!this.taskData || this.taskData.length === 0) {
                return
            }

            // 为每个任务找到对应的日期范围
            this.taskData.forEach(task => {
                const startDate = dayjs(task.execStartDate)
                const endDate = dayjs(task.execEndDate)

                // 找到该任务覆盖的所有日期（包括跨月的情况）
                this.calendarDays.forEach((day, index) => {
                    if (!day.isEmpty && day.date) {
                        const currentDate = dayjs(day.date)
                        if (currentDate.isBetween(startDate, endDate, 'day', '[]')) {
                            day.task = task
                        }
                    }
                })
            })
        },

        // 获取任务数据
        async fetchTaskData() {
            console.log('开始获取任务数据', 'greenhouseId', this.greenhouseId, 'varietyId', this.varietyId, 'yearMonth', this.yearMonth);

            try {
                const response = await axios.post(`${baseUrl}/api/digital-farming/task/allot-record/calendar-data`, {
                    greenhouseId: Number(this.greenhouseId),
                    varietyId: this.varietyId ? this.varietyId : 0,
                    yearMonth: this.yearMonth
                })
                // 处理不同的响应格式
                if (response.data.length > 0) {
                    if (Array.isArray(response.data)) {
                        // 直接返回数组格式
                        this.taskData = response.data
                    }
                    // 重新应用任务数据到日历
                    this.applyTaskData()
                } else {
                    console.log('没有任务数据');
                    this.taskData = []
                    // 重新应用任务数据到日历（清空任务显示）
                    this.applyTaskData()
                }
            } catch (error) {
                console.error('获取日历任务数据失败:', error)
                this.taskData = []
                // 重新应用任务数据到日历（清空任务显示）
                this.applyTaskData()
            }
        },

        // 获取日期样式类
        getDayClass(day) {
            return {
                'other-month': !day.isCurrentMonth && !day.isEmpty,
                'empty': day.isEmpty,
                'has-task': day.task
            }
        },

        // 获取日期背景样式
        getDayBackgroundStyle(day) {
            // 空日期不设置背景色
            if (day.isEmpty) {
                return {};
            }

            // 移除任务背景色，让任务条单独显示
            return {};
        },

        // 获取日期上半部分背景样式
        getDaySectionStyle(day) {
            if (day.isEmpty) {
                return {};
            }

            // 如果有任务，任务优先级最高，使用任务状态背景色
            if (day.task) {
                const task = day.task;
                const status = task.execStatus;

                // 根据任务状态设置背景色
                if (status === 'COMPLETED') {
                    return { backgroundColor: 'rgba(33, 255, 81, 0.40)' }; // 已完成
                } else if (status === 'DELAYED') {
                    return { backgroundColor: 'rgba(212, 249, 215, 0.40)' }; // 已延期
                } else if (status === 'WAITING') {
                    return { backgroundColor: 'rgba(255, 92, 33, 0.40)' }; // 待执行
                }

                // 默认颜色（待执行）
                return { backgroundColor: 'rgba(255, 92, 33, 0.40)' };
            } else {
                // 没有任务时，根据生长时期设置背景色
                return this.getGrowthStageBackgroundColor(day);
            }
        },

        // 根据生长时期获取背景色
        getGrowthStageBackgroundColor(day) {
            if (!this.currentStageInfo || !this.currentStageInfo.allCycleDetailOptions || this.currentStageInfo.allCycleDetailOptions.length === 0) {
                return { backgroundColor: 'rgba(33, 255, 203, 0.40)' }; // 默认背景色
            }

            const dayDate = dayjs(day.date);

            // 遍历所有生长时期，找到当前日期所属的时期
            for (let i = 0; i < this.currentStageInfo.allCycleDetailOptions.length; i++) {
                const stage = this.currentStageInfo.allCycleDetailOptions[i];
                const stageStart = dayjs(stage.startDate);
                const stageEnd = dayjs(stage.endDate);

                // 检查日期是否在当前生长时期内
                if (dayDate.isBetween(stageStart, stageEnd, 'day', '[]')) {
                    return this.getStageBackgroundColor(i);
                }
            }

            // 如果不在任何生长时期内，返回默认色
            return { backgroundColor: 'rgba(33, 255, 203, 0.40)' };
        },

        // 获取生长时期背景色（根据索引）
        getStageBackgroundColor(stageIndex) {
            // 循环使用8种颜色
            const colorIndex = stageIndex % 8;

            // 定义不同时期的背景色（与left.vue中的颜色一致）
            const stageColors = [
                'rgba(212, 249, 215, 0.40)', // 0 - 浅绿色
                'rgba(140, 230, 143, 0.40)', // 1 - 绿色
                'rgba(0, 218, 167, 0.40)',   // 2 - 青绿色
                'rgba(0, 150, 200, 0.40)',   // 3 - 青绿色
                'rgba(218, 58, 0, 0.40)',    // 4 - 红色
                'rgba(218, 156, 0, 0.40)',   // 5 - 橙色
                'rgba(218, 116, 0, 0.40)',   // 6 - 橙红色
                'rgba(0, 127, 218, 0.40)'    // 7 - 蓝色
            ];

            return { backgroundColor: stageColors[colorIndex] };
        },

        // 获取任务背景色
        getTaskBackgroundColor(task) {
            const colors = {
                1: 'rgba(156, 163, 175, 0.2)', // 浅灰色
                2: 'rgba(34, 197, 94, 0.2)', // 绿色
                3: 'rgba(249, 115, 22, 0.2)', // 橙红色
                4: 'rgba(245, 158, 11, 0.2)', // 橙色
                5: 'rgba(59, 130, 246, 0.2)', // 蓝色
                10: 'rgba(34, 197, 94, 0.3)' // 绿色（cycleDetailId: 10）
            }

            return colors[task.cycleDetailId] || 'rgba(34, 197, 94, 0.2)'
        },

        // 获取任务状态
        getTaskStatus(task) {
            const statusMap = {
                'WAITING': '待执行',
                'COMPLETED': '已完成',
                'DELAYED': '已延期'
            }

            return statusMap[task.execStatus] || '待执行'
        },

        // 获取任务状态颜色
        getTaskStatusColor(task) {
            if (task.execStatus === 'COMPLETED') {
                return '#22c55e' // 绿色
            } else if (task.execStatus === 'DELAYED') {
                return '#ef4444' // 红色
            } else {
                return '#ffffff' // 白色（WAITING状态）
            }
        },

        // 获取任务条样式
        getTaskBarStyle(task) {
            const taskStartDate = dayjs(task.execStartDate)
            const taskEndDate = dayjs(task.execEndDate)

            // 找到任务在当前月份日历中的显示范围
            let startIndex = -1
            let endIndex = -1
            let visibleStartDate = null
            let visibleEndDate = null

            // 确定任务在当前月份的可视范围
            const monthStart = this.monthStart
            const monthEnd = this.monthEnd

            // 如果任务开始日期在当前月份之前，则从月份第一天开始显示
            if (taskStartDate.isBefore(monthStart)) {
                visibleStartDate = monthStart
            } else {
                visibleStartDate = taskStartDate
            }

            // 如果任务结束日期在当前月份之后，则到月份最后一天结束显示
            if (taskEndDate.isAfter(monthEnd)) {
                visibleEndDate = monthEnd
            } else {
                visibleEndDate = taskEndDate
            }

            // 找到可视范围在日历中的位置
            this.calendarDays.forEach((day, index) => {
                if (!day.isEmpty && day.date) {
                    const currentDate = dayjs(day.date)
                    if (currentDate.isSame(visibleStartDate, 'day')) {
                        startIndex = index
                    }
                    if (currentDate.isSame(visibleEndDate, 'day')) {
                        endIndex = index
                    }
                }
            })

            if (startIndex === -1 || endIndex === -1) {
                return { display: 'none' }
            }

            // 计算任务在日历网格中的位置
            const startRow = Math.floor(startIndex / 7)
            const startCol = startIndex % 7
            const endRow = Math.floor(endIndex / 7)
            const endCol = endIndex % 7

            let width = 0
            let left = 0

            if (startRow === endRow) {
                // 任务在同一行
                width = (endCol - startCol + 1) * (100 / 7)
                left = startCol * (100 / 7)
            } else {
                // 任务跨越多行，只显示第一行部分
                width = (7 - startCol) * (100 / 7)
                left = startCol * (100 / 7)
            }

            // 根据屏幕高度动态调整任务条位置
            const screenHeight = window.innerHeight
            const dayHeight = this.getResponsiveDayHeight(screenHeight)
            const taskBarHeight = this.getResponsiveTaskBarHeight(screenHeight)

            return {
                position: 'absolute',
                left: `${left}%`,
                width: `${width}%`,
                top: `${(startRow + 1) * dayHeight - taskBarHeight}px`,
                height: `${taskBarHeight}px`,
                zIndex: 100,
                // backgroundColor: '#002043', // 去掉背景色
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
                color: '#ffffff',
                fontSize: this.getResponsiveFontSize(screenHeight)
            }
        },

        // 根据屏幕高度获取响应式的日期格子高度
        getResponsiveDayHeight(screenHeight) {
            // 标准屏幕 1920*1080
            if (screenHeight >= 1080) {
                return 50
            }
            if (screenHeight >= 911) {
                return 49
            }
            // 常见笔记本屏幕 1366*768
            else if (screenHeight >= 765) {
                return 40
            }
            // 小屏幕 1024*768
            else if (screenHeight >= 600) {
                return 35
            }
            // 更小的屏幕
            else {
                return 30
            }
        },

        // 根据屏幕高度获取响应式的任务条高度
        getResponsiveTaskBarHeight(screenHeight) {
            console.log('screenHeight', screenHeight);
            
            // 标准屏幕 1920*1080
            if (screenHeight >= 1080) {
                return 20
            }
            if (screenHeight >= 911) {
                return 18
            }
            // 常见笔记本屏幕 1366*768
            else if (screenHeight >= 765) {
                return 16
            }
            // 小屏幕 1024*768
            else if (screenHeight >= 600) {
                return 14
            }
            // 更小的屏幕
            else {
                return 12
            }
        },

        // 根据屏幕高度获取响应式的字体大小
        getResponsiveFontSize(screenHeight) {
            // 标准屏幕 1920*1080
            if (screenHeight >= 1080) {
                return '10px'
            }
            // 常见笔记本屏幕 1366*768
            else if (screenHeight >= 768) {
                return '9px'
            }
            // 小屏幕 1024*768
            else if (screenHeight >= 600) {
                return '8px'
            }
            // 更小的屏幕
            else {
                return '7px'
            }
        },

        // 获取任务条提示内容
        getTaskTooltipContent(task) {
            const statusMap = {
                'WAITING': '待执行',
                'COMPLETED': '已完成',
                'DELAYED': '已延期'
            }
            const status = statusMap[task.execStatus] || '待执行'
            const startDate = dayjs(task.execStartDate).format('YYYY-MM-DD')
            const endDate = dayjs(task.execEndDate).format('YYYY-MM-DD')
            const duration = dayjs(task.execEndDate).diff(dayjs(task.execStartDate), 'day') + 1

            return `任务名称：${task.categoryName}\n当前状态：${status}\n开始时间：${startDate}\n结束时间：${endDate}\n持续天数：${duration}天\n大棚编号：${task.greenhouseName}\n草莓品种：${task.varietyName}`
        },

        // 暴露dayjs方法给模板使用
        dayjs(date) {
            return dayjs(date)
        },

        // 判断日期是否是任务的开始日期
        isTaskStartDay(day) {
            if (!day.task) {
                return false;
            }
            const taskStartDate = dayjs(day.task.execStartDate);
            return day.date === taskStartDate.format('YYYY-MM-DD');
        },

        // 判断日期是否是任务段的开始日期
        isSegmentStartDay(day) {
            if (day.isEmpty) {
                return false;
            }

            // 获取当前日期的索引
            const currentIndex = this.calendarDays.findIndex(d => d === day);
            if (currentIndex === -1) return false;

            // 获取前一个非空日期
            let prevDay = null;
            for (let i = currentIndex - 1; i >= 0; i--) {
                if (!this.calendarDays[i].isEmpty) {
                    prevDay = this.calendarDays[i];
                    break;
                }
            }

            // 如果是第一个非空日期，则是段开始
            if (!prevDay) {
                return true;
            }

            // 比较当前日期和前一个日期的任务状态
            const currentHasTask = !!day.task;
            const prevHasTask = !!prevDay.task;

            // 如果任务状态发生变化，则是新段开始
            if (currentHasTask !== prevHasTask) {
                return true;
            }

            // 如果都有任务，但是任务不同，则是新段开始
            if (currentHasTask && prevHasTask) {
                return day.task.id !== prevDay.task.id;
            }

            return false;
        },

        // 获取任务开始标识的样式
        getTaskStartMarkerStyle(day) {
            let backgroundColor;

            if (day.task) {
                const task = day.task;
                const status = task.execStatus;

                // 根据任务状态设置色块颜色（不透明版本）
                if (status === 'COMPLETED') {
                    backgroundColor = 'rgb(33, 255, 81)'; // 已完成 - 绿色（不透明）
                } else if (status === 'DELAYED') {
                    backgroundColor = 'rgb(212, 249, 215)'; // 已延期 - 浅绿色（不透明）
                } else if (status === 'WAITING') {
                    backgroundColor = 'rgb(255, 92, 33)'; // 待执行 - 橙色（不透明）
                } else {
                    backgroundColor = 'rgb(255, 92, 33)'; // 默认 - 橙色（不透明）
                }
            } else {
                // 没有任务时，根据生长时期设置标识色
                backgroundColor = this.getGrowthStageMarkerColor(day);
            }

            return {
                position: 'absolute',
                left: '0',
                top: '0',
                width: '4px',
                height: '100%',
                backgroundColor: backgroundColor,
                zIndex: 2
            };
        },

        // 获取生长时期标识色（不透明版本）
        getGrowthStageMarkerColor(day) {
            if (!this.currentStageInfo || !this.currentStageInfo.allCycleDetailOptions || this.currentStageInfo.allCycleDetailOptions.length === 0) {
                return 'rgb(33, 255, 203)'; // 默认色
            }

            const dayDate = dayjs(day.date);

            // 遍历所有生长时期，找到当前日期所属的时期
            for (let i = 0; i < this.currentStageInfo.allCycleDetailOptions.length; i++) {
                const stage = this.currentStageInfo.allCycleDetailOptions[i];
                const stageStart = dayjs(stage.startDate);
                const stageEnd = dayjs(stage.endDate);

                // 检查日期是否在当前生长时期内
                if (dayDate.isBetween(stageStart, stageEnd, 'day', '[]')) {
                    return this.getStageMarkerColor(i);
                }
            }

            // 如果不在任何生长时期内，返回默认色
            return 'rgb(33, 255, 203)';
        },

        // 获取生长时期标识色（不透明版本）
        getStageMarkerColor(stageIndex) {
            // 循环使用8种颜色
            const colorIndex = stageIndex % 8;

            // 定义不同时期的标识色（不透明版本）
            const stageColors = [
                'rgb(212, 249, 215)', // 0 - 浅绿色
                'rgb(140, 230, 143)', // 1 - 绿色
                'rgb(0, 218, 167)',   // 2 - 青绿色
                'rgb(0, 150, 200)',   // 3 - 青蓝色
                'rgb(218, 58, 0)',    // 4 - 红色
                'rgb(218, 156, 0)',   // 5 - 橙色
                'rgb(218, 116, 0)',   // 6 - 橙红色
                'rgb(0, 127, 218)'    // 7 - 蓝色
            ];

            return stageColors[colorIndex];
        },

        // 获取任务条文字颜色
        getTaskMarkerColor(task) {
            if (task.execStatus === 'COMPLETED') {
                return 'rgb(33, 255, 81)'; // 已完成 - 绿色（与色块一致）
            } else if (task.execStatus === 'DELAYED') {
                return 'rgb(212, 249, 215)'; // 已延期 - 浅绿色（与色块一致）
            } else if (task.execStatus === 'WAITING') {
                return 'rgb(255, 92, 33)'; // 待执行 - 橙色（与色块一致）
            } else {
                return 'rgb(255, 92, 33)'; // 默认 - 橙色（与色块一致）
            }
        },

        // 获取指定日期对应的生长阶段信息
        getGrowthStageForDay(day) {
            if (day.isEmpty || !day.date) {
                return null;
            }

            if (!this.currentStageInfo || !this.currentStageInfo.allCycleDetailOptions || this.currentStageInfo.allCycleDetailOptions.length === 0) {
                return null;
            }

            const dayDate = dayjs(day.date);

            // 遍历所有生长时期，找到当前日期所属的时期
            for (let i = 0; i < this.currentStageInfo.allCycleDetailOptions.length; i++) {
                const stage = this.currentStageInfo.allCycleDetailOptions[i];
                const stageStart = dayjs(stage.startDate);
                const stageEnd = dayjs(stage.endDate);

                // 检查日期是否在当前生长时期内
                if (dayDate.isBetween(stageStart, stageEnd, 'day', '[]')) {
                    return stage;
                }
            }

            return null;
        }
    }
}
</script>

<style lang="less" scoped>
.task-bar {
    height: 20px;
}

.calendar-container {
    width: 100%;
    background: rgba(0, 11, 47, 0.3);
    border-radius: 8px;

    .calendar-header {
        display: grid;
        height: 36px;
        grid-template-columns: repeat(7, 1fr);
        background: rgba(3, 21, 41, 0.80);
        // 去掉边框

        .weekday {
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(0, 11, 47, 0.8);
            color: #ffffff;
            font-weight: bold;
            font-size: 14px;
        }
    }

    .calendar-body {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        // 去掉格子间的间隙
        position: relative;
        // 确保子元素可以跨越显示
        overflow: visible;
        // 确保任务内容可以跨越显示
        gap: 0;
        // 确保任务内容可以跨越显示
        isolation: isolate;

        .calendar-day {
            height: 50px; // 默认高度，会被JavaScript动态调整
            margin-bottom: 0px;
            position: relative;
            background: rgba(0, 11, 47, 0.5);
            display: flex;
            flex-direction: column;
            transition: background-color 0.3s ease;
            overflow: visible;
            // 确保任务内容可以跨越显示
            z-index: 1;
            // 确保任务内容可以跨越显示
            isolation: isolate;

            &.other-month {
                opacity: 0.3;
            }

            &.empty {
                background: transparent;
                border: none;
            }

            &.has-task {
                // 去掉边框
            }

            .day-section {
                height: 30px; // 默认高度，会被JavaScript动态调整
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;

                .day-number {
                    color: #ffffff;
                    font-size: 20px;
                    font-weight: 400;
                }

                .task-start-marker {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 4px;
                    height: 100%;
                    z-index: 2;
                }
            }

            .task-section {
                height: 20px; // 默认高度，会被JavaScript动态调整
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                // 确保任务内容可以跨越显示
                overflow: visible;
                z-index: 2;
                // 确保任务内容可见
                background-color: transparent;
            }

            &.has-task .task-section {
                background-color: transparent;
            }

            // 日期tooltip触发器样式
            .day-tooltip-trigger {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 5;
                cursor: pointer;
            }

            .task-content {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: nowrap;
                text-align: center;
                padding: 0 2px;
                width: 100%;
                min-width: 0;
                z-index: 10;
                // 允许内容跨越到其他格子
                overflow: visible;
                // 确保文本不会被截断
                white-space: nowrap;
                // 设置最小宽度确保文本完整显示
                min-width: max-content;
                // 确保任务内容跨越显示
                pointer-events: none;
                // 设置背景色
                background-color: #002043;
                border-radius: 4px;
                // 确保内容可见
                color: #ffffff;
                // 确保任务内容跨越显示
                transform: translateZ(0);
                // 确保任务内容跨越显示
                contain: none;

                .task-info {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-wrap: nowrap;
                    width: 100%;
                    min-width: max-content;
                    text-align: center;
                    gap: 0;
                }

                i {
                    font-size: 12px;
                    // 移除固定颜色，使用动态颜色
                }

                .el-icon-success {
                    // 移除固定颜色，使用动态颜色
                }

                .el-icon-time {
                    // 移除固定颜色，使用动态颜色
                }

                .task-status {
                    font-size: 10px;
                    font-weight: bold;
                    line-height: 1.2;
                    flex-shrink: 0;
                    // 移除固定颜色，使用动态颜色
                }

                .task-separator {
                    font-size: 10px;
                    flex-shrink: 0;
                    // 移除固定颜色，使用动态颜色
                }

                .task-name {
                    font-size: 10px;
                    line-height: 1.2;
                    white-space: nowrap;
                    min-width: 0;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    // 移除固定颜色，使用动态颜色
                }
            }
        }

        // 任务条样式
        .task-bar {
            position: absolute;
            height: 20px;
            // background-color: #002043; // 去掉背景色
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 100;
            // 确保可以接收鼠标事件
            pointer-events: auto;
            cursor: pointer;

            // &:hover {
            //     background-color: #003366; // 去掉hover背景色
            // }

            .task-bar-content {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 2px;
                padding: 0 4px;
                width: 100%;
                // 确保可以接收鼠标事件
                pointer-events: auto;

                i {
                    font-size: 12px;
                    // 移除固定颜色，使用动态颜色
                }

                .el-icon-success {
                    // 移除固定颜色，使用动态颜色
                }

                .el-icon-time {
                    // 移除固定颜色，使用动态颜色
                }

                .task-status {
                    font-size: 10px;
                    font-weight: bold;
                    line-height: 1.2;
                    flex-shrink: 0;
                    // 移除固定颜色，使用动态颜色
                }

                .task-separator {
                    font-size: 10px;
                    flex-shrink: 0;
                    // 移除固定颜色，使用动态颜色
                }

                .task-name {
                    font-size: 10px;
                    line-height: 1.2;
                    white-space: nowrap;
                    min-width: 0;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    // 移除固定颜色，使用动态颜色
                }
            }
        }
    }
}
</style>
<style lang="less">
.task-tooltip {
    max-width: 300px !important;

    .tooltip-content {
        padding: 8px 0;

        .tooltip-title {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 8px;
            color: #ffffff;
            border-bottom: 1px solid rgba(255, 255, 255, 0.2);
            padding-bottom: 4px;
        }

        .tooltip-item {
            display: flex;
            margin-bottom: 4px;
            line-height: 1.4;

            .tooltip-label {
                color: #b0b0b0;
                min-width: 70px;
                flex-shrink: 0;
            }

            .tooltip-value {
                color: #ffffff;
                flex: 1;
            }
        }
    }
}
</style>